import React, { useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import { useIntl } from '../../util/reactIntl';
import { subscribeNewsletter } from '../../util/api';

import css from './NewsletterSignup.module.css';

/**
 * Reusable newsletter signup form. Drop it in the footer, homepage, or a
 * landing page via the `source` prop (used for consent provenance).
 *
 * MVP fields: email (required) + first name (optional). Handles duplicates and
 * re-subscription cleanly server-side; the UI just reflects the outcome.
 */
const NewsletterSignup = props => {
  const { className, rootClassName, source = 'footer', title, description } = props;
  const intl = useIntl();

  const [email, setEmail] = useState('');
  const [status, setStatus] = useState('idle'); // idle | submitting | success | error
  const [message, setMessage] = useState('');

  const t = (id, defaultMessage, values) => intl.formatMessage({ id, defaultMessage }, values);

  const handleSubmit = async e => {
    e.preventDefault();
    if (status === 'submitting') return;
    const trimmed = email.trim();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      setStatus('error');
      setMessage(t('NewsletterSignup.invalidEmail', 'Veuillez saisir une adresse email valide.'));
      return;
    }
    setStatus('submitting');
    setMessage('');
    try {
      const res = await subscribeNewsletter({ email: trimmed, source });
      setStatus('success');
      if (res.alreadySubscribed) {
        setMessage(t('NewsletterSignup.already', 'Vous êtes déjà inscrit à notre newsletter.'));
      } else {
        setMessage(t('NewsletterSignup.success', 'Merci ! Vous êtes bien inscrit à notre newsletter.'));
      }
      setEmail('');
    } catch (err) {
      setStatus('error');
      setMessage(
        err && err.message
          ? err.message
          : t('NewsletterSignup.error', "L'inscription a échoué. Réessayez plus tard.")
      );
    }
  };

  const classes = classNames(rootClassName || css.root, className);
  const isSuccess = status === 'success';

  return (
    <div className={classes}>
      {title !== null ? (
        <h3 className={css.title}>{title || t('NewsletterSignup.title', 'Recevez nos dernières nouveautés')}</h3>
      ) : null}
      <p className={css.legal}>
        {description ||
          t(
            'NewsletterSignup.legal',
            'Recevez nos nouveautés, guides et bons plans maison connectée. Désinscription en un clic, à tout moment.'
          )}
      </p>

      {isSuccess ? (
        <p className={css.successMessage} role="status">
          {message}
        </p>
      ) : (
        <form className={css.form} onSubmit={handleSubmit} noValidate>
          <div className={css.inputGroup}>
            <input
              type="email"
              className={css.input}
              placeholder={t('NewsletterSignup.emailPlaceholder', 'Votre adresse email')}
              value={email}
              onChange={e => setEmail(e.target.value)}
              required
              autoComplete="email"
              aria-label={t('NewsletterSignup.emailPlaceholder', 'Votre adresse email')}
            />
            <button type="submit" className={css.button} disabled={status === 'submitting'}>
              {status === 'submitting'
                ? t('NewsletterSignup.submitting', 'Inscription…')
                : t('NewsletterSignup.submit', "Je m'inscris")}
            </button>
          </div>
          {status === 'error' ? (
            <p className={css.errorMessage} role="alert">
              {message}
            </p>
          ) : null}
        </form>
      )}
    </div>
  );
};

NewsletterSignup.propTypes = {
  className: PropTypes.string,
  rootClassName: PropTypes.string,
  source: PropTypes.string,
  title: PropTypes.node,
  description: PropTypes.node,
};

export default NewsletterSignup;
